import { ChangeEvent, FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Modal from '@components/Modal/Modal';
import useToggle from '@hooks/useToggle';
import { handleShareInquiryResponse } from '@store/features/inquiries/share/actions';
import {
  inquiriesInquiryToShareSelector,
  inquiriesShareSelectedSupplementsSelector,
  inquiriesShareUrlIsLoadingSelector,
  inquiriesShareUrlSelector,
} from '@store/features/inquiries/share/selectors';
import {
  changeAllMedications,
  changeAllSupplements,
  removeSharedInquiryResponseDetails,
  updateListOfItems,
} from '@store/features/inquiries/share/slice';
import useDispatch from '@store/hooks/useDispatch';

import GeneratedLink from './GeneratedLink/GeneratedLink';
import SelectSupplements from './SelectSupplements/SelectSupplements';

import './style.scss';

const ShareResponseModal: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isModalOpen, onModalOpen, onModalClose] = useToggle();

  const inquiryToShare = useSelector(inquiriesInquiryToShareSelector);
  const shareInquirySupplements = useSelector(inquiriesShareSelectedSupplementsSelector);
  const shortUrl = useSelector(inquiriesShareUrlSelector);
  const isLoading = useSelector(inquiriesShareUrlIsLoadingSelector);

  useEffect(() => {
    inquiryToShare && onModalOpen();
  }, [onModalOpen, inquiryToShare]);

  const handleShare = useCallback(() => {
    if (inquiryToShare && shareInquirySupplements) {
      dispatch(
        handleShareInquiryResponse({
          inquiryId: inquiryToShare.id,
          ...(inquiryToShare.isSupplementary && {
            supplementInquiriesIds: shareInquirySupplements,
          }),
          ...(!inquiryToShare.isSupplementary && {
            medicationInquiriesIds: shareInquirySupplements,
          }),
        }),
      );
    }
  }, [dispatch, inquiryToShare, shareInquirySupplements]);

  const handleChangeSupplements = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === 'all') {
      dispatch(changeAllSupplements(event.target.checked));
    } else {
      dispatch(
        updateListOfItems({
          value: Number(event.target.value),
          checked: event.target.checked,
        }),
      );
    }
  }, []);

  const handleChangeMedication = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === 'all') {
      dispatch(changeAllMedications(event.target.checked));
    } else {
      dispatch(
        updateListOfItems({
          value: Number(event.target.value),
          checked: event.target.checked,
        }),
      );
    }
  }, []);

  const handleExit = useCallback(() => {
    dispatch(removeSharedInquiryResponseDetails());
  }, [dispatch]);

  useEffect(() => () => handleExit(), [handleExit]);

  const modalProps = useMemo(() => {
    if (!inquiryToShare) {
      return {};
    }
    const { isSupplementary } = inquiryToShare;
    return shortUrl
      ? {
          className: 'share-response-modal share-response-modal_generated-link',
          title: isSupplementary
            ? t('shareResponse.generatedLinkTitle')
            : t('shareResponse.generatedMedicationLinkTitle'),
          subTitle: isSupplementary
            ? t('shareResponse.generatedLinkDescription')
            : t('shareResponse.generatedMedicationLinkDescription'),
          onLeftAction: undefined,
          onRightAction: onModalClose,
          leftActionTitle: undefined,
          rightActionTitle: t('common.done'),
        }
      : {
          className: 'share-response-modal share-response-modal_select-supplements',
          title: isSupplementary
            ? t('shareResponse.selectSupplementsTitle')
            : t('shareResponse.selectMedicationsTitle'),
          subTitle: isSupplementary
            ? t('shareResponse.selectSupplementsDescription')
            : t('shareResponse.selectMedicationsDescription'),
          onLeftAction: onModalClose,
          onRightAction: handleShare,
          leftActionTitle: t('common.cancel'),
          rightActionTitle: t('common.next'),
          isRightActionDisabled: !shareInquirySupplements?.length,
          isRightActionLoading: isLoading,
        };
  }, [shortUrl, inquiryToShare, shareInquirySupplements, isLoading, handleShare, onModalClose]);

  return (
    <Modal
      {...modalProps}
      onClose={onModalClose}
      onExited={handleExit}
      open={isModalOpen}
      contentClass="share-response-modal__body-content"
      icon={<img className="share-response-modal__icon" src="/images/img-weblink.svg" />}
    >
      {shortUrl ? (
        <GeneratedLink />
      ) : (
        <SelectSupplements
          onChange={
            inquiryToShare?.isSupplementary ? handleChangeSupplements : handleChangeMedication
          }
        />
      )}
    </Modal>
  );
};

export default ShareResponseModal;
